import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'wouter';
import moment from 'moment';

import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  IdentificationIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import Swal from 'sweetalert2';

import { AuthContext } from '../contexts/auth';
import { getJustificationProfiles, deleteJustificationProfile } from '../services/api_justification_profiles';
import { v2EstraiMessaggiErrore } from '../services/utils';
import CustomTooltipComponent from './custom_tooltip_component';


export default function JustificationProfile() {
  const { token } = useContext(AuthContext);

  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingProfileId, setDeletingProfileId] = useState(null);
  const [error, setError] = useState('');
  const [showInfo, setShowInfo] = useState(false);


  /* =========================================================
    LOAD
  ========================================================= */
  const fetchProfiles = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    setError('');

    try {
      const data = await getJustificationProfiles(token);
      setProfiles(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error('Errore caricamento profili giustificativi:', error);

      if (error?.response?.status !== 402) {
        setError(v2EstraiMessaggiErrore(error));
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);


  /* =========================================================
    DELETE
  ========================================================= */
  const handleDelete = async (profile) => {
    const ousCount = profile?.organizational_units?.length ?? profile?.ous_count ?? 0;
    const contractsCount = profile?.contracts?.length ?? profile?.contracts_count ?? 0;
    const hasAssignments = ousCount > 0 || contractsCount > 0;

    const assignments = [];

    if (ousCount > 0) {
      assignments.push(`${ousCount} ${ousCount === 1 ? 'unità organizzativa' : 'unità organizzative'}`);
    }

    if (contractsCount > 0) {
      assignments.push(`${contractsCount} ${contractsCount === 1 ? 'contratto' : 'contratti'}`);
    }

    const result = await Swal.fire({
      title: 'Eliminare il profilo?',
      html: hasAssignments
        ? `
          Stai per eliminare il profilo <strong>${profile.name}</strong>.<br><br>
          Il profilo è attualmente assegnato a <strong>${assignments.join(' e ')}</strong>.<br><br>
          Eliminandolo verranno rimosse anche tutte le relative associazioni.<br><br>
          Sei sicuro di voler procedere?
        `
        : `
          Stai per eliminare il profilo <strong>${profile.name}</strong>.<br><br>
          Sei sicuro di voler procedere?
        `,
      icon: hasAssignments ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: 'Elimina',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#dc2626',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setDeletingProfileId(profile.id);

    try {
      await deleteJustificationProfile(token, profile.id);

      setProfiles((prev) => prev.filter((item) => item.id !== profile.id));

      await Swal.fire({
        title: 'Profilo eliminato',
        text: hasAssignments
          ? 'Il profilo e le relative associazioni sono stati eliminati'
          : 'Il profilo è stato eliminato',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Errore eliminazione profilo:', error);

      await Swal.fire({
        title: 'Impossibile eliminare il profilo',
        text: v2EstraiMessaggiErrore(error) || 'Si è verificato un errore durante l’eliminazione',
        icon: 'error',
        confirmButtonText: 'Chiudi',
      });
    } finally {
      setDeletingProfileId(null);
    }
  };


  /* =========================================================
    RENDER
  ========================================================= */
  return (
    <div>
      {/* HEADER */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">

        <div>
          <h3 className="text-sm font-semibold text-gray-800">
            Profili giustificativi
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Definisci quali giustificativi possono essere richiesti dai dipendenti
          </p>

          <button
            type="button"
            onClick={() => setShowInfo((prev) => !prev)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            <InformationCircleIcon className="h-4 w-4" />

            A cosa servono i gruppi di giustificativi?

            <ChevronDownIcon
              className={`h-3.5 w-3.5 transition-transform ${
                showInfo ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
        {!(!isLoading && !error && profiles.length === 0) && (
        <Link
          href="/justification_profile"
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          <PlusIcon className="h-4 w-4 shrink-0" />
          Crea nuovo gruppo
        </Link>
        )}
      </div>

      {/* INFO */}
      {showInfo && (
          <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/50 px-4 py-3">
              <div className="flex gap-3">
                  <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />

                  <div>
                      <p className="text-xs leading-5 text-gray-700">
                          I gruppi permettono di definire quali giustificativi un dipendente può selezionare quando effettua una richiesta.
                      </p>

                      <div className="mt-2 space-y-1 text-xs leading-5 text-gray-600">
                          <p>
                              <strong className="font-semibold text-gray-700">Unità organizzativa:</strong>{' '}
                              il dipendente può utilizzare i giustificativi dei gruppi assegnati direttamente alle unità organizzative di cui fa parte, in base al contratto attivo. I gruppi non vengono ereditati dalle unità organizzative superiori.
                          </p>

                          <p>
                              <strong className="font-semibold text-gray-700">Contratto:</strong>{' '}
                              se al contratto del dipendente è assegnato un gruppo, questo prevale su quelli delle unità organizzative. Il dipendente potrà richiedere esclusivamente i giustificativi contenuti nel gruppo associato al contratto.
                          </p>

                          <p>
                              <strong className="font-semibold text-gray-700">Nessun gruppo assegnato:</strong>{' '}
                              se non sono presenti gruppi associati al contratto o alle unità organizzative di appartenenza, il dipendente può richiedere i giustificativi abilitati dall'azienda nella sezione "Giustificativi".
                          </p>

                          <p>
                              <strong className="font-semibold text-gray-700">Gestori:</strong>{' '}
                              i gestori autorizzati alla gestione dei giustificativi possono assegnare, modificare o rimuovere l'associazione dei gruppi ai dipendenti e alle unità organizzative che rientrano nel proprio ambito di visualizzazione. Non possono creare nuovi gruppi di giustificativi.
                          </p>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* ERROR */}
      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}


      {/* LOADING */}
      {isLoading && (
        <div className="py-10 text-center">
          <p className="text-sm text-gray-500">
            Caricamento profili...
          </p>
        </div>
      )}


      {/* EMPTY */}
      {!isLoading && !error && profiles.length === 0 && (
        <div className="mt-5 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">

          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <ClipboardDocumentListIcon className="h-5 w-5 text-indigo-600" />
          </div>

          <h4 className="mt-3 text-sm font-semibold text-gray-800">
            Nessun profilo configurato
          </h4>

          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">
            Crea un profilo per raggruppare i giustificativi e gestirne le assegnazioni
          </p>

          <Link
            href="/justification_profile"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
          >
            <PlusIcon className="h-4 w-4" />
            Crea primo profilo
          </Link>

        </div>
      )}


      {/* PROFILES */}
      {!isLoading && profiles.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-lg border border-gray-300 bg-white">
          <div className="divide-y divide-indigo-700">
            {profiles.map((profile) => (
              <ProfileRow
                key={profile.id}
                profile={profile}
                isDeleting={deletingProfileId === profile.id}
                onDelete={() => handleDelete(profile)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* =========================================================
  PROFILE ROW
========================================================= */
function ProfileRow({ profile, isDeleting, onDelete }) {
  const [showDetails, setShowDetails] = useState(false);

  const justifications = profile?.justifications || [];
  const organizationalUnits = profile?.organizational_units || [];
  const contracts = profile?.contracts || [];

  const ousCount = profile?.ous_count ?? organizationalUnits.length;
  const contractsCount = profile?.contracts_count ?? contracts.length;

  return (
    <div className="px-4 py-4 transition-colors bg-indigo-50/30 hover:bg-gray-50 sm:px-5">
      {/* HEADER ROW */}
      <div className="flex items-center justify-between gap-4">
        {/* TITLE */}
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h4 className="text-sm font-semibold uppercase text-indigo-800">
            {profile.name}
          </h4>

          <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-indigo-700">
            {justifications.length}{' '}
            {justifications.length === 1 ? 'giustificativo' : 'giustificativi'}
          </span>
        </div>


        {/* ACTIONS */}
        <div className="flex shrink-0 items-center gap-1">
          <CustomTooltipComponent title="Modifica">
            <span className="inline-flex">
              <Link
                href={`/justification_profile/${profile.id}`}
                className={`rounded-md p-2 text-gray-800 hover:bg-indigo-50 hover:text-indigo-600 ${
                  isDeleting ? 'pointer-events-none opacity-40' : ''
                }`}
              >
                <PencilSquareIcon className="h-5 w-5" />
              </Link>
            </span>
          </CustomTooltipComponent>

          <CustomTooltipComponent
            title={isDeleting ? 'Eliminazione...' : 'Elimina'}
          >
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className="rounded-md p-2 text-gray-800 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </CustomTooltipComponent>
        </div>
      </div>

      {/* JUSTIFICATIONS */}
      {justifications.length > 0 ? (
        <div className="mt-3 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
          {/* TABLE HEADER */}
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-3 py-2">
            <div className="flex items-center gap-2">
              <ClipboardDocumentListIcon className="h-4 w-4 text-indigo-600" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                Giustificativi inclusi
              </span>
            </div>
            <span className="text-[11px] font-medium text-gray-600">
              {justifications.length}
            </span>
          </div>


          {/* TABLE BODY */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
            {justifications.map((item) => (
              <div
                key={item.id}
                title={item.justification?.description || ''}
                className="flex min-w-0 items-center gap-2 border-b border-r border-gray-200 px-3 py-1.5 transition-colors hover:bg-indigo-50/50 sm:[&:nth-child(2n)]:border-r-0 xl:[&:nth-child(2n)]:border-r xl:[&:nth-child(3n)]:border-r-0"
              >

                {/* CODE */}
                <span className="w-[55px] shrink-0 text-[11px] font-bold uppercase tracking-wide text-indigo-700">
                  {item.justification?.code}
                </span>

                {/* DESCRIPTION */}
                <span className="min-w-0 truncate text-xs text-gray-700">
                  {item.justification?.description || '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-3 w-full rounded-md border border-dashed border-gray-200 bg-white px-3 py-3">
          <p className="text-[11px] italic text-gray-400">
            Nessun giustificativo nel profilo
          </p>
        </div>

      )}

      {/* FOOTER */}
      <div className="mt-3 flex w-full flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <p className="text-xs text-gray-700">
            Collegamenti:
          </p>

          {/* ORGANIZATIONAL UNITS COUNT */}
          <div className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <BuildingOffice2Icon className="h-4 w-4 text-indigo-700" />
            <span>
              <strong className="font-semibold text-gray-700">{ousCount}</strong>{' '}
              {ousCount === 1 ? 'unità organizzativa' : 'unità organizzative'}
            </span>
          </div>

          {/* CONTRACTS COUNT */}
          {contractsCount > 0 && (
            <div className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <IdentificationIcon className="h-4 w-4 text-indigo-700" />
              <span>
                <strong className="font-semibold text-gray-700">
                  {contractsCount}
                </strong>{' '}
                {contractsCount === 1 ? 'contratto' : 'contratti'}
              </span>
            </div>
          )}
        </div>

        {/* DETAILS TOGGLE */}
        {(ousCount > 0 || contractsCount > 0) && (
          <button
            type="button"
            onClick={() => setShowDetails((prev) => !prev)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 hover:underline"
          >
            {showDetails ? 'Nascondi dettagli' : 'Visualizza dettagli'}
            {showDetails ? (
              <ChevronUpIcon className="h-3.5 w-3.5" />
            ) : (
              <ChevronDownIcon className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>
      
      {/* DETAILS */}
      {showDetails && (
        <div className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
          {/* ORGANIZATIONAL UNITS */}
          {organizationalUnits.length > 0 && (
            <AssignmentSection
              title="Unità organizzative"
              icon={BuildingOffice2Icon}
              count={organizationalUnits.length}
            >
              {organizationalUnits.map((unit) => (
                <div
                  key={unit.id}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
                >
                  <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0 text-gray-600" />

                  <span className="text-xs font-medium text-gray-700">
                    {unit.name}
                  </span>
                </div>
              ))}
            </AssignmentSection>
          )}

          {/* CONTRACTS */}
          {contracts.length > 0 && (
            <AssignmentSection
              title="Contratti"
              icon={IdentificationIcon}
              count={contracts.length}
              borderTop={organizationalUnits.length > 0}
            >
              {contracts.map((contract) => {
                const fullName = [contract.employee_last_name, contract.employee_first_name].filter(Boolean).join(' ');
                const hasDates = contract.start_date || contract.end_date;

                return (
                  <Link
                    key={contract.id}
                    to={`/dipendenti/${contract.employee_cf}/edit?tab=2`}
                    className="block w-fit cursor-pointer"
                  >
                    <div className="rounded-md cursor-pointer border border-gray-200 bg-gray-50 px-3 py-1 transition-colors hover:border-indigo-300 hover:bg-indigo-50">

                      {/* NAME / CF */}
                      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                        <IdentificationIcon className="h-3.5 w-3.5 shrink-0 text-gray-700" />

                        <span>
                          {fullName || contract.employee_cf}
                        </span>
                      </div>

                      {/* CF */}
                      {fullName && contract.employee_cf && (
                        <div className="mt-0.5 text-xs text-gray-600">
                          {contract.employee_cf}
                        </div>
                      )}

                      {/* CONTRACT DATES */}
                      {hasDates && (
                        <div className="text-[10px] text-gray-600">
                          {contract.start_date && (
                            <>
                              Dal {moment(contract.start_date).format('DD/MM/YYYY')}
                            </>
                          )}

                          {contract.start_date && contract.end_date && (
                            <> al </>
                          )}

                          {contract.end_date && (
                            <>
                              {contract.start_date
                                ? moment(contract.end_date).format('DD/MM/YYYY')
                                : `Fino al ${moment(contract.end_date).format('DD/MM/YYYY')}`}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </AssignmentSection>
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================
  ASSIGNMENT SECTION
========================================================= */
function AssignmentSection({
  title,
  icon: Icon,
  count,
  emptyText,
  children,
  borderTop = false,
}) {
  return (
    <div className={`p-3 ${borderTop ? 'border-t border-gray-200' : ''}`}>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-indigo-600" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
          {title}
        </span>
        <span className="rounded-md bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 ring-1 ring-gray-200">
          {count}
        </span>
      </div>

      {count > 0 ? (
        <div className="flex flex-wrap gap-2">{children}</div>
      ) : (
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs italic text-gray-400">
          {emptyText}
        </div>
      )}
    </div>
  );
}